import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchJson } from "./api";
import { useTranslation } from "react-i18next";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.12)";
const GOLD_BORDER = "rgba(212,165,80,0.3)";
const BG = "#07070a";
const SURFACE = "#0f0f17";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

const inputStyle = {
  width:"100%", background:"rgba(255,255,255,0.04)", border:`1px solid ${BORDER}`,
  borderRadius:8, color:TEXT, fontSize:14, padding:"10px 14px", outline:"none",
  fontFamily:"inherit", boxSizing:"border-box"
};

const RUOLI = ["Assistente al montaggio","Assistente di Produzione","Colorist","Coordinatore di Post Produzione","Editor Video","Finalizzatore","Ingest","Logger","Post Producer","Redattore"];

export default function EventEditPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    title: "", description: "", event_date: "", event_time: "",
    city: "", address: "", is_anonymous: false, visibility: "all",
    visibility_roles: [], max_participants: ""
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  function patch(obj) { setForm(f => ({ ...f, ...obj })); }

  useEffect(() => {
    fetchJson(`/events/${id}`).then(d => {
      const e = d.event;
      const dt = e.event_date ? new Date(e.event_date) : null;
      patch({
        title: e.title || "",
        description: e.description || "",
        event_date: dt ? dt.toISOString().slice(0,10) : "",
        event_time: dt ? dt.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"}) : "",
        city: e.city || "",
        address: e.address || "",
        is_anonymous: e.is_anonymous || false,
        visibility: e.visibility || "all",
        visibility_roles: e.visibility_filter?.roles || [],
        max_participants: e.max_participants || ""
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  async function handleSubmit() {
    if (!form.title.trim()) return setErr(t("enew.err_title"));
    if (!form.event_date) return setErr(t("enew.err_date"));
    setSaving(true); setErr("");
    try {
      const event_date = form.event_time
        ? `${form.event_date}T${form.event_time}:00`
        : `${form.event_date}T20:00:00`;
      const payload = { ...form, event_date };
      if (form.visibility === 'roles') payload.visibility_filter = { roles: form.visibility_roles };
      if (form.max_participants) payload.max_participants = parseInt(form.max_participants);
      await fetchJson(`/events/${id}`, { method: "PUT", body: JSON.stringify(payload) });
      nav(`/eventi/${id}`);
    } catch(e) {
      setErr(e.message || t("enew.edit_err_save"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ background:BG, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", color:TEXT_DIM }}>{t("events.detail_loading")}</div>;

  return (
    <div style={{ minHeight:"100vh", background:BG, color:TEXT, fontFamily:"'Kanit','Inter',sans-serif", padding:"32px 24px" }}>
      <button onClick={() => nav(`/eventi/${id}`)} style={{ background:"none", border:"none", color:TEXT_DIM, cursor:"pointer", fontSize:13, marginBottom:24, padding:0 }}>
        {t("enew.edit_back")}
      </button>

      <h1 style={{ margin:"0 0 32px 0", fontSize:22, fontWeight:800, color:TEXT }}>{t("enew.edit_title")}</h1>

      <div style={{ display:"flex", flexDirection:"column", gap:20, maxWidth:700 }}>
        <div>
          <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_title")}</label>
          <input value={form.title} onChange={e => patch({ title: e.target.value })}
            placeholder={t("enew.ph_title")} style={inputStyle} />
        </div>

        <div>
          <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_desc")}</label>
          <textarea value={form.description} onChange={e => patch({ description: e.target.value })}
            placeholder={t("enew.ph_desc")} rows={4}
            style={{ ...inputStyle, resize:"vertical" }} />
        </div>

        <div style={{ display:"flex", gap:12 }}>
          <div style={{ flex:1 }}>
            <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_date")}</label>
            <input type="date" value={form.event_date} onChange={e => patch({ event_date: e.target.value })}
              style={{ ...inputStyle, background:"#1a1d23", colorScheme:"dark" }} />
          </div>
          <div style={{ flex:1 }}>
            <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_time")}</label>
            <input type="time" value={form.event_time} onChange={e => patch({ event_time: e.target.value })}
              style={{ ...inputStyle, background:"#1a1d23", colorScheme:"dark" }} />
          </div>
        </div>

        <div style={{ display:"flex", gap:12 }}>
          <div style={{ flex:1 }}>
            <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_city")}</label>
            <input value={form.city} onChange={e => patch({ city: e.target.value })} placeholder={t("enew.ph_city")} style={inputStyle} />
          </div>
          <div style={{ flex:2 }}>
            <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_address")}</label>
            <input value={form.address} onChange={e => patch({ address: e.target.value })} placeholder={t("enew.ph_address")} style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_visibility")}</label>
          <select value={form.visibility} onChange={e => patch({ visibility: e.target.value, visibility_roles: [] })}
            style={{ ...inputStyle, background:"#1a1d23", colorScheme:"dark" }}>
            <option value="all">{t("enew.vis_all")}</option>
            <option value="roles">{t("enew.vis_roles")}</option>
          </select>
        </div>

        {form.visibility === "roles" && (
          <div>
            <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_roles")}</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {RUOLI.map(r => {
                const sel = form.visibility_roles.includes(r);
                return (
                  <button key={r} type="button" onClick={() => patch({ visibility_roles: sel ? form.visibility_roles.filter(x=>x!==r) : [...form.visibility_roles, r] })}
                    style={{ background:sel?GOLD_DIM:"rgba(255,255,255,0.03)", border:`1px solid ${sel?GOLD_BORDER:"rgba(255,255,255,0.08)"}`, borderRadius:20, color:sel?GOLD:"rgba(232,230,223,0.55)", fontSize:12, padding:"5px 12px", cursor:"pointer" }}>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <label style={{ fontSize:12, color:TEXT_DIM, display:"block", marginBottom:6 }}>{t("enew.label_max")} <span style={{ color:"rgba(232,230,223,0.3)" }}>{t("enew.max_optional_short")}</span></label>
          <input type="number" min={1} value={form.max_participants} onChange={e => patch({ max_participants: e.target.value })}
            placeholder={t("enew.ph_max_short")} style={inputStyle} />
        </div>

        <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px" }}>
          <label style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
            <input type="checkbox" checked={form.is_anonymous} onChange={e => patch({ is_anonymous: e.target.checked })}
              style={{ accentColor:GOLD, marginTop:3, flexShrink:0 }} />
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:TEXT, marginBottom:4 }}>{t("enew.anon_title")}</div>
              <div style={{ fontSize:12, color:TEXT_DIM, lineHeight:1.5 }}>{t("enew.anon_sub")}</div>
            </div>
          </label>
        </div>

        {err && <div style={{ fontSize:13, color:"#f87171" }}>{err}</div>}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={() => nav(`/eventi/${id}`)}
            style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:8, color:TEXT_DIM, cursor:"pointer", padding:"10px 20px", fontSize:13 }}>
            {t("enew.cancel")}
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ background:GOLD_DIM, border:`1px solid ${GOLD_BORDER}`, borderRadius:8, color:GOLD, cursor:saving?"not-allowed":"pointer", padding:"10px 24px", fontSize:13, fontWeight:700 }}>
            {saving ? t("enew.saving") : t("enew.edit_submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
